import { APIHelpers } from './apiHelpers';

class CoinbaseApi extends APIHelpers {
  constructor() {
    super();
    this.baseUrl = 'https://api.coinbase.com/v2';
  }

  /**
   * Gets the current price and market data for a specific trading pair on Coinbase
   * using the older V2 api.
   *
   * @param {string} currency - The currency symbol to get the price for (e.g., "ETH")
   * @returns {Promise<number|undefined>} The price of the currency in USD or undefined if request fails
   *
   * @example
   * // Example usage:
   * const ethUsdTicker = await coinbaseApi.getCurrencySymbolUsdRate("ETH");
   *
   * // Example response:
   * // "1808.8"
   */
  async getCurrencySymbolUsdRate(currency = 'ETH'): Promise<number | undefined> {
    try {
      this.baseUrl = 'https://api.coinbase.com/v2';
      const response = await this.fetchWithTimeout(`/prices/${currency}-USD/spot`);
      return parseFloat(response.data.amount);
    } catch {
      return undefined;
    }
  }

  /**
   * Gets the current price and market data for a specific trading pair on Coinbase
   *
   * @param {string} productId - Trading pair identifier (e.g., "ETH-USD", "BTC-EUR")
   * @returns {Promise<object|undefined>} Market data for the specified product or undefined if request fails
   *
   * @example
   * // Example usage:
   * const ethUsdTicker = await coinbaseApi.getCoinbaseTickerByProductId("ETH-USD");
   *
   * // Example response:
   * // {
   * //   ask: "1808.72",        // Best ask price
   * //   bid: "1808.43",        // Best bid price
   * //   volume: "193357.23",   // 24h trading volume
   * //   trade_id: 629386170,   // Latest trade ID
   * //   price: "1808.8",       // Latest trade price
   * //   size: "0.01",          // Latest trade size
   * //   time: "2025-04-04T15:11:03.906154Z", // Timestamp
   * //   rfq_volume: "1854.78"  // Request for quote volume
   * // }
   */
  async getCoinbaseTickerByProductId(productId: string): Promise<
    | {
        ask: string;
        bid: string;
        volume: string;
        trade_id: number;
        price: string;
        size: string;
        time: string;
        rfq_volume: string;
      }
    | undefined
  > {
    try {
      this.baseUrl = 'https://api.exchange.coinbase.com';
      const response = await this.fetchWithTimeout(`/products/${productId}/ticker`);
      return response.data;
    } catch {
      return undefined;
    }
  }

  /**
   * Retrieves product information from Coinbase API
   *
   * This function can be used to get the product_id for a given token pair,
   * which can then be used with the ticker endpoint:
   * https://api.exchange.coinbase.com/products/{product_id}/ticker
   *
   * @returns {Promise<Array<ProductInfo>>} Array of product information objects
   *
   * @example
   * // Example response data:
   * {
   *   "id": "NEAR-USDT",
   *   "base_currency": "NEAR",
   *   "quote_currency": "USDT",
   *   "quote_increment": "0.001",
   *   "base_increment": "0.001",
   *   "display_name": "NEAR-USDT",
   *   "min_market_funds": "1",
   *   "margin_enabled": false,
   *   "post_only": false,
   *   "limit_only": false,
   *   "cancel_only": false,
   *   "status": "online",
   *   "status_message": "",
   *   "trading_disabled": false,
   *   "fx_stablecoin": false,
   *   "max_slippage_percentage": "0.03000000",
   *   "auction_mode": false,
   *   "high_bid_limit_percentage": ""
   * }
   */
  async getAllCoinbaseProducts(): Promise<
    | {
        id: string;
        base_currency: string;
        quote_currency: string;
        quote_increment: string;
        base_increment: string;
        display_name: string;
        min_market_funds: string;
        margin_enabled: boolean;
        post_only: boolean;
        limit_only: boolean;
        cancel_only: boolean;
        status: string;
        status_message: string;
        trading_disabled: boolean;
        fx_stablecoin: boolean;
        max_slippage_percentage: string;
        auction_mode: boolean;
        high_bid_limit_percentage: string;
      }[]
    | undefined
  > {
    try {
      this.baseUrl = 'https://api.exchange.coinbase.com';
      const response = await this.fetchWithTimeout('/products');
      return response.data;
    } catch {
      return undefined;
    }
  }
}

export const coinbaseApi = new CoinbaseApi();
